/**
 * ============================================================================
 * GUIDED TOUR SERVICE
 * ============================================================================
 *
 * Central service for managing guided tour state using NgRX SignalStore.
 * Provides methods for:
 * - Starting/stopping tours
 * - Navigating between steps
 * - Managing highlights, tooltips, and explanation cards
 * - Computing element positions for overlays
 *
 * @module tour
 * @version 1.0.0
 * @created 2026-01-07
 */

import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { filter, fromEvent, Observable, race, take, timer } from 'rxjs';

import {
    HighlightRect,
    initialTourState,
    TourConfig,
    TourExplanationCard,
    TourHighlight,
    TourTooltip,
} from './tour.models';

// =============================================================================
// TOUR STORE (SignalStore)
// =============================================================================

/**
 * Central SignalStore for tour state management.
 * Provides reactive state and methods for controlling guided tours.
 */
export const TourStore = signalStore(
    { providedIn: 'root' },
    withState(initialTourState),
    withComputed((store) => ({
        /**
         * Get the current step configuration.
         */
        currentStep: computed(() => {
            const tour = store.currentTour();
            const index = store.currentStepIndex();
            return tour?.steps[index] ?? null;
        }),

        /**
         * Check if there's a next step available.
         */
        hasNextStep: computed(() => {
            const tour = store.currentTour();
            const index = store.currentStepIndex();
            return tour ? index < tour.steps.length - 1 : false;
        }),

        /**
         * Check if there's a previous step available.
         */
        hasPrevStep: computed(() => {
            const index = store.currentStepIndex();
            return index > 0;
        }),

        /**
         * Get total number of steps in the current tour.
         */
        totalSteps: computed(() => {
            const tour = store.currentTour();
            return tour?.steps.length ?? 0;
        }),

        /**
         * Get human-readable progress (e.g., "Step 2 of 5").
         */
        progressText: computed(() => {
            const tour = store.currentTour();
            const index = store.currentStepIndex();
            if (!tour) return '';
            return `Step ${index + 1} of ${tour.steps.length}`;
        }),
    })),
    withMethods((store) => ({
        /**
         * Start a new tour with the given configuration.
         */
        startTour(config: TourConfig): void {
            if (store.isActive()) {
                this.endTour();
            }

            patchState(store, {
                isActive: true,
                currentTour: config,
                currentStepIndex: 0,
                isPaused: false,
            });

            config.onStart?.();
        },

        /**
         * Navigate to the next step.
         * @returns true if navigation succeeded, false if already at last step
         */
        nextStep(): boolean {
            const tour = store.currentTour();
            const currentIndex = store.currentStepIndex();

            if (!tour || currentIndex >= tour.steps.length - 1) {
                return false;
            }

            const previousIndex = currentIndex;
            const newIndex = currentIndex + 1;

            // Call onLeave for current step
            tour.steps[currentIndex]?.onLeave?.();

            patchState(store, { currentStepIndex: newIndex });

            // Call onEnter for new step
            tour.steps[newIndex]?.onEnter?.();

            // Call onStepChange callback
            tour.onStepChange?.(newIndex, previousIndex);

            return true;
        },

        /**
         * Navigate to the previous step.
         * @returns true if navigation succeeded, false if already at first step
         */
        prevStep(): boolean {
            const tour = store.currentTour();
            const currentIndex = store.currentStepIndex();

            if (!tour || currentIndex <= 0) {
                return false;
            }

            const previousIndex = currentIndex;
            const newIndex = currentIndex - 1;

            // Call onLeave for current step
            tour.steps[currentIndex]?.onLeave?.();

            patchState(store, { currentStepIndex: newIndex });

            // Call onEnter for new step
            tour.steps[newIndex]?.onEnter?.();

            // Call onStepChange callback
            tour.onStepChange?.(newIndex, previousIndex);

            return true;
        },

        /**
         * Jump to a specific step by index.
         */
        goToStep(index: number): void {
            const tour = store.currentTour();
            if (!tour || index < 0 || index >= tour.steps.length) {
                return;
            }

            const previousIndex = store.currentStepIndex();
            tour.steps[previousIndex]?.onLeave?.();

            patchState(store, { currentStepIndex: index });

            tour.steps[index]?.onEnter?.();
            tour.onStepChange?.(index, previousIndex);
        },

        /**
         * End the tour and reset state.
         * @param completed Whether the tour was completed (vs skipped)
         */
        endTour(completed = false): void {
            const tour = store.currentTour();
            const currentIndex = store.currentStepIndex();

            if (tour) {
                // Call onLeave for current step
                tour.steps[currentIndex]?.onLeave?.();

                // Call completion or skip callback
                if (completed) {
                    tour.onComplete?.();
                } else {
                    tour.onSkip?.();
                }
            }

            patchState(store, {
                isActive: false,
                currentTour: null,
                currentStepIndex: 0,
                activeHighlights: [],
                activeTooltip: null,
                activeCard: null,
                isPaused: false,
            });
        },

        /**
         * Complete the tour (calls onComplete callback).
         */
        completeTour(): void {
            this.endTour(true);
        },

        /**
         * Skip the tour (calls onSkip callback).
         */
        skipTour(): void {
            this.endTour(false);
        },

        /**
         * Pause the tour (e.g., during transitions).
         */
        pause(): void {
            patchState(store, { isPaused: true });
        },

        /**
         * Resume a paused tour.
         */
        resume(): void {
            patchState(store, { isPaused: false });
        },

        /**
         * Update computed highlight rectangles.
         * Called by TourHighlightOverlay when element positions change.
         */
        updateHighlights(highlights: HighlightRect[]): void {
            patchState(store, { activeHighlights: highlights });
        },

        /**
         * Set the active tooltip (used for standalone tooltip mode).
         */
        setTooltip(tooltip: TourTooltip | null): void {
            patchState(store, { activeTooltip: tooltip });
        },

        /**
         * Set the active explanation card (used for standalone card mode).
         */
        setExplanationCard(card: TourExplanationCard | null): void {
            patchState(store, { activeCard: card });
        },

        // =========================================================================
        // STANDALONE MODE METHODS
        // These allow using individual features without a full tour configuration.
        // =========================================================================

        /**
         * Show highlights for specific elements without starting a full tour.
         * Useful for one-off highlighting needs.
         */
        showHighlights(highlights: TourHighlight[]): void {
            // Create a minimal tour configuration
            const config: TourConfig = {
                id: '_standalone_highlights_',
                steps: [{
                    id: '_standalone_step_',
                    highlights,
                }],
            };

            patchState(store, {
                isActive: true,
                currentTour: config,
                currentStepIndex: 0,
            });
        },

        /**
         * Show a tooltip without starting a full tour.
         */
        showTooltip(tooltip: TourTooltip): void {
            patchState(store, {
                isActive: true,
                activeTooltip: tooltip,
            });
        },

        /**
         * Show an explanation card without starting a full tour.
         */
        showExplanationCard(card: TourExplanationCard, highlights?: TourHighlight[]): void {
            const config: TourConfig = {
                id: '_standalone_card_',
                steps: [{
                    id: '_standalone_step_',
                    highlights,
                    explanationCard: card,
                }],
            };

            patchState(store, {
                isActive: true,
                currentTour: config,
                currentStepIndex: 0,
                activeCard: card,
            });
        },

        /**
         * Hide all tour elements and reset to inactive state.
         */
        hideAll(): void {
            patchState(store, {
                isActive: false,
                currentTour: null,
                currentStepIndex: 0,
                activeHighlights: [],
                activeTooltip: null,
                activeCard: null,
                isPaused: false,
            });
        },
    }))
);

// =============================================================================
// TOUR ELEMENT SERVICE
// =============================================================================

/**
 * Utility service for resolving element positions and computing highlight rects.
 * Separated from the store for cleaner architecture and testing.
 */
@Injectable({ providedIn: 'root' })
export class TourElementService {
    private readonly document = inject(DOCUMENT);

    /**
     * Resolve a CSS selector to an HTMLElement.
     * @returns The element or null if not found.
     */
    resolveElement(selector: string): HTMLElement | null {
        try {
            return this.document.querySelector<HTMLElement>(selector);
        } catch (e) {
            console.warn(`[TourElementService] Invalid selector: ${selector}`, e);
            return null;
        }
    }

    /**
     * Wait for an element to appear in the DOM.
     * @param selector CSS selector to wait for
     * @param timeout Maximum wait time in ms (default 5000)
     * @returns Promise resolving to the element or null if timeout
     */
    waitForElement(selector: string, timeout = 5000): Promise<HTMLElement | null> {
        const element = this.resolveElement(selector);
        if (element) {
            return Promise.resolve(element);
        }

        const elementFound$ = new Observable<HTMLElement>((subscriber) => {
            const observer = new MutationObserver(() => {
                const el = this.resolveElement(selector);
                if (el) {
                    observer.disconnect();
                    subscriber.next(el);
                    subscriber.complete();
                }
            });

            observer.observe(this.document.body, {
                childList: true,
                subtree: true,
            });

            return () => observer.disconnect();
        });

        const timeout$ = timer(timeout);

        return new Promise((resolve) => {
            race(elementFound$, timeout$)
                .pipe(take(1))
                .subscribe((result) => {
                    if (typeof result === 'number') {
                        // Timeout occurred, check one last time
                        resolve(this.resolveElement(selector));
                    } else {
                        resolve(result);
                    }
                });
        });
    }

    /**
     * Compute the highlight rectangle for a given highlight configuration.
     * @returns HighlightRect with computed positions, or null if element not found.
     */
    computeHighlightRect(highlight: TourHighlight): HighlightRect | null {
        const element = this.resolveElement(highlight.selector);
        if (!element) {
            console.warn(`[TourElementService] Element not found: ${highlight.selector}`);
            return null;
        }

        const rect = element.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(element);
        const padding = highlight.padding ?? 4;

        // Compute border radius
        let borderRadius: number;
        if (highlight.borderRadius === 'auto' || highlight.borderRadius === undefined) {
            // Extract from computed style (take the first value if shorthand)
            const radiusStr = computedStyle.borderRadius;
            borderRadius = parseFloat(radiusStr) || 0;
        } else {
            borderRadius = highlight.borderRadius;
        }

        // Add padding to border radius proportionally
        borderRadius = borderRadius + padding * 0.5;

        return {
            id: highlight.id,
            x: rect.left - padding,
            y: rect.top - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
            borderRadius,
            glowColor: highlight.glowColor ?? 'var(--primary-color)',
        };
    }

    /**
     * Compute highlight rectangles for multiple highlights.
     * Filters out any that couldn't be resolved.
     */
    computeHighlightRects(highlights: TourHighlight[]): HighlightRect[] {
        return highlights
            .map((h) => this.computeHighlightRect(h))
            .filter((rect): rect is HighlightRect => rect !== null);
    }

    /**
     * Get the center point of an element for connector line targeting.
     */
    getElementCenter(selector: string): { x: number; y: number } | null {
        const element = this.resolveElement(selector);
        if (!element) return null;

        const rect = element.getBoundingClientRect();
        return {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
        };
    }

    /**
     * Get the best edge point for a connector line based on the card position.
     * Returns the point on the element's edge closest to the given source point.
     */
    getElementEdgePoint(
        selector: string,
        sourcePoint: { x: number; y: number }
    ): { x: number; y: number } | null {
        const element = this.resolveElement(selector);
        if (!element) return null;

        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // Determine which edge to connect to based on relative position
        const dx = sourcePoint.x - centerX;
        const dy = sourcePoint.y - centerY;

        // Choose the edge based on the dominant direction
        if (Math.abs(dx) > Math.abs(dy)) {
            // Connect to left or right edge
            return {
                x: dx > 0 ? rect.right : rect.left,
                y: centerY,
            };
        } else {
            // Connect to top or bottom edge
            return {
                x: centerX,
                y: dy > 0 ? rect.bottom : rect.top,
            };
        }
    }
}

// =============================================================================
// TOUR KEYBOARD SERVICE
// =============================================================================

/**
 * Service that handles keyboard interactions for the tour.
 * Listens for Escape key to dismiss active tours.
 */
@Injectable({ providedIn: 'root' })
export class TourKeyboardService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly tourStore = inject(TourStore);
    private readonly document = inject(DOCUMENT);

    constructor() {
        fromEvent<KeyboardEvent>(this.document, 'keydown')
            .pipe(
                filter((e) => e.key === 'Escape'),
                filter(() => this.tourStore.isActive()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.tourStore.endTour());
    }
}
